import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { SiteToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useSettingMutations } from '~/hooks/use-settings';
import { settingSchema, EMPTY_SETTING, type SettingFormValues } from '~/lib/schemas/setting';
import { isKnownLoyaltySetting } from '~/lib/loyalty-settings';

/** Sentinel Select value for a base (SiteId = null) setting — Radix Select disallows an empty value. */
const BASE_SITE = '__base__';

interface SettingEditDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The setting being edited, or null when adding a new one. */
	initial: SettingFormValues | null;
	/** Distinct known setting names for the ParamName datalist suggestions. */
	names: string[];
	sites: SiteToken[];
}

/**
 * Add / edit a setting. The ParamName field is a hybrid input (free text + a datalist of known
 * names), matching the legacy admin's searchable-select-with-custom-value editor. `wsid` selects
 * a site override or "base" (null).
 */
export function SettingEditDialog({ open, onOpenChange, initial, names, sites }: SettingEditDialogProps) {
	const { t } = useTranslation();
	const { create, update } = useSettingMutations();
	const form = useForm<SettingFormValues>({
		resolver: zodResolver(settingSchema) as Resolver<SettingFormValues>,
		defaultValues: initial ?? EMPTY_SETTING,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);
	// The platform reads loyalty settings by name: an existing one keeps it.
	const nameFixed = isEdit && isKnownLoyaltySetting(initial?.pn);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_SETTING);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const wsid = watch('wsid');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.settings.editTitle') : t('system.settings.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.settings.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.settings.columns.name')} error={formState.errors.pn?.message}>
						<Input
							{...register('pn')}
							list="setting-name-options"
							maxLength={80}
							aria-invalid={Boolean(formState.errors.pn)}
							readOnly={nameFixed}
							className={nameFixed ? 'bg-muted' : undefined}
							autoFocus={!nameFixed}
						/>
						<datalist id="setting-name-options">
							{names.map(n => (
								<option key={n} value={n} />
							))}
						</datalist>
					</FormField>
					<FormField label={t('system.settings.columns.value')} error={formState.errors.pv?.message}>
						<Input {...register('pv')} aria-invalid={Boolean(formState.errors.pv)} />
					</FormField>
					<FormField label={t('system.settings.columns.site')}>
						<Select
							value={wsid === null ? BASE_SITE : String(wsid)}
							onValueChange={v => setValue('wsid', v === BASE_SITE ? null : Number(v), { shouldDirty: true })}>
							<SelectTrigger className="w-full" aria-label={t('system.settings.columns.site')}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={BASE_SITE}>{t('system.settings.baseSite')}</SelectItem>
								{sites.map(s => (
									<SelectItem key={s.id} value={String(s.id)}>
										{s.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<FormField label={t('system.fields.paramGroup')}>
						<Input {...register('prmg')} />
					</FormField>
					<FormField label={t('system.fields.description')}>
						<Textarea rows={2} {...register('descr')} />
					</FormField>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
